import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, Save, AlertTriangle, RotateCcw, Lock, Pencil, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";

const INT_EXT_OPTIONS = ["INT", "EXT", "INT/EXT"];
const TIME_OF_DAY_OPTIONS = [
    "DAY",
    "NIGHT",
    "DAWN",
    "DUSK",
    "MORNING",
    "EVENING",
    "CONTINUOUS",
    "LATER",
];

/**
 * Owner-only Scene Edit dialog.
 * Props:
 *   projectId, scene (current doc), open, onClose, onSaved(updatedScene)
 *   autoFocusField — optional: 'look' | 'description' | 'heading'.
 *                    Defaults to no auto-focus so the dialog never
 *                    lands the cursor on Episode (destructive field
 *                    accidents were the iter-121 pilot report).
 */
export default function SceneEditDialog({ projectId, scene, open, onClose, onSaved, autoFocusField = null }) {
    const apiBase = useApiBase();
    const [form, setForm] = useState({
        heading: "",
        episode: "",
        int_ext: "",
        time_of_day: "",
        description: "",
        story_time: "",
        // Iter-120 — scene-level Look (overall scene state).
        look: "",
    });
    const [omitted, setOmitted] = useState(false);
    const [headingUnlocked, setHeadingUnlocked] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [showOmitConfirm, setShowOmitConfirm] = useState(false);
    // Iter-121 — structural-edit confirmation. When the user changes
    // episode or heading we require an explicit "yes, change it"
    // before PATCH so on-set muscle memory doesn't silently break
    // numbering / episode alignment.
    const [showStructuralConfirm, setShowStructuralConfirm] = useState(false);

    // Refs for targeted auto-focus. Prevents Radix's default
    // "focus first input" behaviour from landing on Episode.
    const lookRef = useRef(null);
    const descriptionRef = useRef(null);
    const headingRef = useRef(null);

    useEffect(() => {
        if (!open || !scene) return;
        setForm({
            heading: scene.heading === "OMITTED" ? "" : scene.heading || "",
            episode: scene.episode || "",
            int_ext: scene.int_ext || "",
            time_of_day: scene.time_of_day || "",
            description: scene.description || "",
            story_time: scene.story_time || "",
            look: scene.look || "",
        });
        setOmitted(Boolean(scene.omitted));
        setHeadingUnlocked(false);
        setError("");
        setShowOmitConfirm(false);
        setShowStructuralConfirm(false);
        // Iter-121 — targeted auto-focus. Runs after the dialog
        // paint so the ref is mounted; small delay beats fighting
        // Radix's focus trap.
        if (autoFocusField) {
            const t = setTimeout(() => {
                if (autoFocusField === "look") lookRef.current?.focus();
                else if (autoFocusField === "description") descriptionRef.current?.focus();
                else if (autoFocusField === "heading") {
                    setHeadingUnlocked(true);
                    setTimeout(() => headingRef.current?.focus(), 30);
                }
            }, 80);
            return () => clearTimeout(t);
        }
    // Iter-158.13.2.20f.19b — Description-truncation repair. The
    // original deps `[open, scene, autoFocusField]` fired every
    // ~12s while the dialog was open because SceneDetail's
    // background polling (usePolling(loadContinuity, 12s)) hands
    // down a fresh `scene` object reference on every refetch —
    // wiping the user's mid-typing back to the last-saved state.
    // Guarding on `scene?.id` instead means we hydrate once when
    // the dialog opens onto a scene, then leave the form alone
    // until the user closes and reopens. Same-scene polling can no
    // longer clobber typing.
    }, [open, scene?.id, autoFocusField]);

    // Iter-121 — structural edit detection. Episode, heading and
    // omitted are the fields the user can silently trash; flag them
    // so we can gate Save behind an explicit confirmation modal.
    const structuralChanged =
        scene &&
        ((form.episode || "") !== (scene.episode || "") ||
            (headingUnlocked && form.heading.trim() !==
                ((scene.heading === "OMITTED" ? "" : scene.heading) || "").trim()));

    const dirty =
        scene &&
        (form.heading !== (scene.heading === "OMITTED" ? "" : scene.heading || "") ||
            form.episode !== (scene.episode || "") ||
            form.int_ext !== (scene.int_ext || "") ||
            form.time_of_day !== (scene.time_of_day || "") ||
            form.description !== (scene.description || "") ||
            form.story_time !== (scene.story_time || "") ||
            form.look !== (scene.look || "") ||
            omitted !== Boolean(scene.omitted));

    const willNewlyOmit = omitted && !scene?.omitted;

    const save = async () => {
        if (!scene) return;
        setError("");
        setSaving(true);
        try {
            // Scene number is immutable — never send it.
            const payload = {
                heading: omitted
                    ? form.heading.trim() || "OMITTED"
                    : form.heading.trim() || scene.heading || "Untitled scene",
                episode: omitted ? null : (form.episode.trim() || null),
                int_ext: omitted ? null : form.int_ext || null,
                time_of_day: omitted ? null : form.time_of_day || null,
                description: omitted ? null : (form.description.trim() || null),
                story_time: omitted ? null : (form.story_time.trim() || null),
                look: omitted ? null : (form.look.trim() || null),
                omitted,
            };
            const { data } = await api.patch(
                `${apiBase}/${projectId}/scenes/${scene.id}`,
                payload,
            );
            toast.success(
                omitted && !scene.omitted
                    ? "Scene marked omitted"
                    : !omitted && scene.omitted
                      ? "Scene reactivated"
                      : "Scene updated",
            );
            // Let any list view (On Set / Prep) know to refetch.
            try {
                window.dispatchEvent(
                    new CustomEvent("onsetflow:scene-updated", {
                        detail: { sceneId: scene.id },
                    }),
                );
            } catch (_err) {
                /* ignore */
            }
            onSaved?.(data);
            onClose?.();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const handlePrimaryClick = () => {
        if (willNewlyOmit) {
            setShowOmitConfirm(true);
            return;
        }
        // Iter-121 — intercept structural changes with an explicit
        // confirmation. Users on a hot set occasionally tap Edit to
        // add a Look and walk away typing a scene number into the
        // Episode field — this blocks that silent destruction.
        if (structuralChanged) {
            setShowStructuralConfirm(true);
            return;
        }
        save();
    };

    const confirmOmitAndSave = () => {
        setShowOmitConfirm(false);
        save();
    };

    const confirmStructuralAndSave = () => {
        setShowStructuralConfirm(false);
        save();
    };

    return (
        <Dialog open={open} onOpenChange={(v) => (!v && !saving ? onClose?.() : null)}>
            <DialogContent
                data-testid="scene-edit-dialog"
                className="max-w-md w-full bg-card border-white/10 max-h-[90dvh] sm:max-h-[88vh] flex flex-col p-0 overflow-hidden"
                onOpenAutoFocus={(e) => {
                    // Iter-121 — Radix's default is "focus the first
                    // focusable element" which is the Episode field.
                    // We suppress it and let our own ref-driven
                    // auto-focus (see useEffect) place the cursor on
                    // the correct field for the current use case.
                    e.preventDefault();
                }}
            >
                <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-white/5">
                    <DialogTitle className="font-display text-2xl font-bold text-white">
                        Edit scene
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Existing media, notes and characters are preserved.
                    </DialogDescription>
                </DialogHeader>

                {/* Iter-157C — body scrolls independently so the
                    footer buttons never push below the viewport on
                    short / mobile screens. Uses 90dvh on mobile to
                    respect dynamic browser chrome. */}
                <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                    <div className="space-y-5">
                    {omitted ? (
                        <div
                            data-testid="scene-edit-omitted-banner"
                            className="flex items-start gap-3 rounded-lg border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-amber-200"
                        >
                            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                            <div className="text-sm">
                                This scene is marked omitted. It stays in the list for
                                numbering context but is non-shootable.
                            </div>
                        </div>
                    ) : null}

                    <div>
                        <Label
                            htmlFor="scene-edit-episode"
                            className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Episode <span className="lowercase tracking-normal text-neutral-600">(optional · TV)</span>
                        </Label>
                        <Input
                            id="scene-edit-episode"
                            data-testid="scene-edit-episode-input"
                            value={form.episode}
                            disabled={omitted}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    episode: e.target.value,
                                }))
                            }
                            placeholder="e.g. 1 or Pilot — leave blank for film"
                            maxLength={20}
                            className="h-12 mt-2 bg-secondary border-transparent text-base"
                        />
                    </div>

                    <div className="grid grid-cols-[110px_1fr] gap-3 items-start">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                                Scene Number
                            </Label>
                            <div
                                data-testid="scene-edit-number-display"
                                className="h-12 mt-2 px-3 rounded-md bg-secondary/60 border border-white/10 text-lg font-semibold text-white text-center flex items-center justify-center mono"
                                title="Scene number is permanent and cannot be changed"
                            >
                                {scene?.scene_number || "—"}
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-600 mt-1.5 text-center">
                                Locked
                            </p>
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor="scene-edit-heading"
                                    className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                >
                                    Heading
                                </Label>
                                {!omitted ? (
                                    <button
                                        type="button"
                                        data-testid={
                                            headingUnlocked
                                                ? "scene-edit-heading-lock"
                                                : "scene-edit-heading-unlock"
                                        }
                                        onClick={() =>
                                            setHeadingUnlocked((v) => !v)
                                        }
                                        className="tap inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white"
                                    >
                                        {headingUnlocked ? (
                                            <>
                                                <Lock size={11} />
                                                Lock
                                            </>
                                        ) : (
                                            <>
                                                <Pencil size={11} />
                                                Edit
                                            </>
                                        )}
                                    </button>
                                ) : null}
                            </div>
                            {headingUnlocked && !omitted ? (
                                <Input
                                    ref={headingRef}
                                    id="scene-edit-heading"
                                    data-testid="scene-edit-heading-input"
                                    value={form.heading}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            heading: e.target.value,
                                        }))
                                    }
                                    placeholder="INT. APARTMENT - KITCHEN - DAY"
                                    className="h-12 mt-2 bg-secondary border-transparent text-base"
                                />
                            ) : (
                                <div
                                    data-testid="scene-edit-heading-display"
                                    className="mt-2 px-3 py-2.5 min-h-12 rounded-md bg-secondary/60 border border-white/10 text-base text-white leading-snug break-words [overflow-wrap:anywhere] whitespace-normal"
                                    title={
                                        omitted
                                            ? "Omitted scenes do not show a heading"
                                            : "Heading is protected. Tap Edit above to change."
                                    }
                                >
                                    {omitted
                                        ? "—"
                                        : form.heading || scene?.heading || "—"}
                                </div>
                            )}
                        </div>
                    </div>

                    {!omitted ? (
                        <div>
                            <Label
                                htmlFor="scene-edit-description"
                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Scene Description
                            </Label>
                            <Textarea
                                ref={descriptionRef}
                                id="scene-edit-description"
                                data-testid="scene-edit-description-input"
                                value={form.description}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        description: e.target.value,
                                    }))
                                }
                                placeholder="Brief summary of what happens in this scene…"
                                rows={3}
                                maxLength={2000}
                                className="mt-2 bg-secondary border-transparent text-base resize-none"
                            />
                        </div>
                    ) : null}

                    {/* Iter-120 — Scene-level Look. The overall visual
                        state of the scene (rain, blood, daytime, etc.).
                        Coexists with per-state-entry character looks. */}
                    {!omitted ? (
                        <div>
                            <Label
                                htmlFor="scene-edit-look"
                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Scene Look <span className="lowercase tracking-normal text-neutral-600">(optional)</span>
                            </Label>
                            <Input
                                ref={lookRef}
                                id="scene-edit-look"
                                data-testid="scene-edit-look-input"
                                value={form.look}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        look: e.target.value,
                                    }))
                                }
                                placeholder="e.g. Rain, Aftermath, Bloody"
                                maxLength={60}
                                className="h-12 mt-2 bg-secondary border-transparent text-base"
                            />
                            <p className="text-[10px] text-neutral-500 mt-1.5">
                                Scene-wide state. Each character can still have their own look variation.
                            </p>
                        </div>
                    ) : null}

                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            INT / EXT
                        </Label>
                        <div
                            data-testid="scene-edit-intext-row"
                            className="mt-2 flex gap-2 flex-wrap"
                        >
                            <SegmentButton
                                active={!form.int_ext}
                                disabled={omitted}
                                testid="scene-edit-intext-none"
                                onClick={() =>
                                    setForm((f) => ({ ...f, int_ext: "" }))
                                }
                            >
                                —
                            </SegmentButton>
                            {INT_EXT_OPTIONS.map((o) => (
                                <SegmentButton
                                    key={o}
                                    active={form.int_ext === o}
                                    disabled={omitted}
                                    testid={`scene-edit-intext-${o.replace("/", "-")}`}
                                    onClick={() =>
                                        setForm((f) => ({ ...f, int_ext: o }))
                                    }
                                >
                                    {o}
                                </SegmentButton>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Time of day
                        </Label>
                        <div
                            data-testid="scene-edit-tod-row"
                            className="mt-2 flex gap-2 flex-wrap"
                        >
                            <SegmentButton
                                active={!form.time_of_day}
                                disabled={omitted}
                                testid="scene-edit-tod-none"
                                onClick={() =>
                                    setForm((f) => ({ ...f, time_of_day: "" }))
                                }
                            >
                                —
                            </SegmentButton>
                            {TIME_OF_DAY_OPTIONS.map((o) => (
                                <SegmentButton
                                    key={o}
                                    active={form.time_of_day === o}
                                    disabled={omitted}
                                    testid={`scene-edit-tod-${o}`}
                                    onClick={() =>
                                        setForm((f) => ({ ...f, time_of_day: o }))
                                    }
                                >
                                    {o}
                                </SegmentButton>
                            ))}
                        </div>
                        {!omitted ? (
                            <div className="mt-3">
                                <Label
                                    htmlFor="scene-edit-story-time"
                                    className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                >
                                    Story Time
                                </Label>
                                <Input
                                    id="scene-edit-story-time"
                                    data-testid="scene-edit-story-time-input"
                                    value={form.story_time}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            story_time: e.target.value,
                                        }))
                                    }
                                    placeholder="e.g. 7:42pm, Day 3, Two hours later"
                                    maxLength={80}
                                    className="h-12 mt-2 bg-secondary border-transparent text-base"
                                />
                                <p className="text-[10px] text-neutral-500 mt-1.5">
                                    Free-text — anything that helps locate this scene in the story timeline.
                                </p>
                            </div>
                        ) : null}
                    </div>

                    <div
                        data-testid="scene-edit-omit-row"
                        className="flex items-center justify-between rounded-xl border border-white/10 bg-secondary/60 px-4 py-3"
                    >
                        <div className="flex items-center gap-3">
                            <AlertTriangle size={18} className="text-amber-300" />
                            <div>
                                <div className="text-sm font-semibold text-white">
                                    {omitted ? "Omitted" : "Active"}
                                </div>
                                <div className="text-xs text-neutral-400">
                                    {omitted
                                        ? "Visible in the list, not shootable."
                                        : "Shootable — on the schedule."}
                                </div>
                            </div>
                        </div>
                        {omitted ? (
                            <Button
                                data-testid="scene-edit-reactivate-btn"
                                type="button"
                                variant="secondary"
                                onClick={() => setOmitted(false)}
                                className="h-11 gap-2"
                            >
                                <RotateCcw size={14} />
                                Reactivate
                            </Button>
                        ) : (
                            <Button
                                data-testid="scene-edit-omit-btn"
                                type="button"
                                variant="secondary"
                                onClick={() => setOmitted(true)}
                                className="h-11 gap-2 text-amber-200 hover:text-amber-100"
                            >
                                Mark omitted
                            </Button>
                        )}
                    </div>

                    {error ? (
                        <p
                            data-testid="scene-edit-error"
                            className="text-sm text-red-400"
                        >
                            {error}
                        </p>
                    ) : null}

                    <div className="flex items-center gap-3 pt-1">
                        <Button
                            data-testid="scene-edit-cancel"
                            type="button"
                            variant="secondary"
                            disabled={saving}
                            onClick={onClose}
                            className="flex-1 min-tap h-12"
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="scene-edit-save"
                            type="button"
                            disabled={!dirty || saving}
                            onClick={handlePrimaryClick}
                            className="flex-1 min-tap h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2"
                        >
                            {saving ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Save changes
                                </>
                            )}
                        </Button>
                    </div>
                    </div>
                </div>

                {showOmitConfirm ? (
                    <div
                        data-testid="scene-edit-omit-confirm"
                        className="absolute inset-0 bg-background/95 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center p-6 text-center gap-4 z-10"
                    >
                        <AlertTriangle size={36} className="text-amber-300" />
                        <div className="space-y-1">
                            <h3 className="font-display text-xl font-bold text-white">
                                Mark this scene omitted?
                            </h3>
                            <p className="text-sm text-neutral-400 max-w-sm">
                                It will stay in the list for numbering context, but
                                non-shootable. Existing media, notes and characters
                                are preserved and will reappear if you reactivate.
                            </p>
                        </div>
                        <div className="flex items-center gap-3 w-full">
                            <Button
                                data-testid="scene-edit-omit-confirm-cancel"
                                type="button"
                                variant="secondary"
                                onClick={() => setShowOmitConfirm(false)}
                                className="flex-1 min-tap h-12"
                            >
                                Keep active
                            </Button>
                            <Button
                                data-testid="scene-edit-omit-confirm-ok"
                                type="button"
                                onClick={confirmOmitAndSave}
                                className="flex-1 min-tap h-12 bg-amber-400 hover:bg-amber-400/90 text-black font-semibold"
                            >
                                Mark omitted
                            </Button>
                        </div>
                    </div>
                ) : null}

                {/* Iter-121 — structural-edit confirmation. Fires when
                    the user has changed Episode or Heading; blocks the
                    save until they explicitly acknowledge the change
                    could disrupt continuity / scene structure. */}
                {showStructuralConfirm ? (
                    <div
                        data-testid="scene-edit-structural-confirm"
                        className="absolute inset-0 bg-background/95 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center p-6 text-center gap-4 z-10"
                    >
                        <ShieldAlert size={36} className="text-amber-300" />
                        <div className="space-y-1">
                            <h3 className="font-display text-xl font-bold text-white">
                                Change scene structure?
                            </h3>
                            <p
                                data-testid="scene-edit-structural-confirm-copy"
                                className="text-sm text-neutral-400 max-w-sm"
                            >
                                Are you sure you want to change this? This may affect
                                continuity and scene structure.
                            </p>
                            <ul className="mt-3 text-[11px] text-amber-200/90 space-y-0.5 inline-block text-left">
                                {(form.episode || "") !== (scene?.episode || "") ? (
                                    <li data-testid="scene-edit-structural-confirm-diff-episode">
                                        · Episode <span className="line-through text-neutral-500">{scene?.episode || "—"}</span> → <span className="font-semibold">{form.episode || "—"}</span>
                                    </li>
                                ) : null}
                                {headingUnlocked && form.heading.trim() !==
                                    ((scene?.heading === "OMITTED" ? "" : scene?.heading) || "").trim() ? (
                                    <li data-testid="scene-edit-structural-confirm-diff-heading">
                                        · Heading changed
                                    </li>
                                ) : null}
                            </ul>
                        </div>
                        <div className="flex items-center gap-3 w-full">
                            <Button
                                data-testid="scene-edit-structural-confirm-cancel"
                                type="button"
                                variant="secondary"
                                onClick={() => setShowStructuralConfirm(false)}
                                className="flex-1 min-tap h-12"
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="scene-edit-structural-confirm-ok"
                                type="button"
                                onClick={confirmStructuralAndSave}
                                className="flex-1 min-tap h-12 bg-amber-400 hover:bg-amber-400/90 text-black font-semibold"
                            >
                                Yes, change it
                            </Button>
                        </div>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

function SegmentButton({ active, disabled, onClick, testid, children }) {
    return (
        <button
            type="button"
            data-testid={testid}
            disabled={disabled}
            onClick={onClick}
            className={`tap min-h-[44px] px-4 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-secondary text-neutral-200 border-white/10 hover:bg-white/10"
            }`}
        >
            {children}
        </button>
    );
}
